/**
 * Il colore del riquadro di un canale, quando il logo non c'è.
 *
 * ## Perché un colore e non un grigio
 *
 * Perché nel prototipo ogni canale ha il **suo**: «Prima» è arancio, «Aurora» viola, «Arena Sport»
 * blu. Non è decorazione — la colonna della guida si legge di lato mentre si scorre, e il colore è
 * quel che permette di ritrovare una riga senza rileggerla. Sedici rettangoli grigi identici, invece,
 * si distinguono solo leggendoli uno per uno.
 *
 * ## Perché il nome e non le iniziali
 *
 * Nel prototipo dentro il riquadro c'è **il nome del canale**, scritto per intero. Io ci avevo messo
 * due iniziali: `IS` per «IT| Sky Sport Uno HD», `J` per «Juventus». Due lettere non identificano
 * niente — su un catalogo vero ci sono venti canali che cominciano per «IT|» — e occupano un riquadro
 * che potrebbe dire il nome. È un ripiego che costa quanto la cosa giusta e vale meno.
 *
 * ## La tavolozza è quella del prototipo
 *
 * Sono i sedici colori dei canali di esempio, presi uno per uno. Il colore del testo sta accanto al
 * fondo e non si calcola: sui tre chiari il prototipo scrive scuro, e una formula di contrasto
 * generica darebbe risultati diversi dal disegno proprio su quei tre.
 */

export interface ChannelColor {
  readonly background: string;
  readonly foreground: string;
}

/** I sedici colori del prototipo, nell'ordine in cui compaiono. */
export const CHANNEL_COLORS: ReadonlyArray<ChannelColor> = [
  { background: '#E4572E', foreground: '#FFFFFF' },
  { background: '#6D55E8', foreground: '#FFFFFF' },
  { background: '#E0B000', foreground: '#1A1400' },
  { background: '#0069A8', foreground: '#FFFFFF' },
  { background: '#1F8277', foreground: '#FFFFFF' },
  { background: '#B3121D', foreground: '#FFFFFF' },
  { background: '#4F7A1A', foreground: '#FFFFFF' },
  { background: '#16695F', foreground: '#FFFFFF' },
  { background: '#1C7A63', foreground: '#FFFFFF' },
  { background: '#80553A', foreground: '#FFFFFF' },
  { background: '#56622F', foreground: '#FFFFFF' },
  { background: '#8E5D3E', foreground: '#FFFFFF' },
  { background: '#34507A', foreground: '#FFFFFF' },
  { background: '#F28482', foreground: '#2A0E0E' },
  { background: '#F4A261', foreground: '#2A1606' },
  { background: '#A3168E', foreground: '#FFFFFF' },
];

/**
 * Il colore di un canale, **sempre lo stesso** per lo stesso identificativo.
 *
 * Deterministico e non casuale: un colore che cambia a ogni disegno è peggio di nessun colore, perché
 * insegna a non fidarsene. E deriva dall'**identificativo**, non dalla posizione: le posizioni
 * cambiano a ogni import del catalogo, e un canale che cambia colore dopo un import è esattamente il
 * genere di cosa che fa dire «l'app si è rotta».
 */
export function channelColorOf(seed: string): ChannelColor {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    // Il classico `hash * 31 + c`, ricondotto a 32 bit con `| 0`: senza, su nomi lunghi il numero
    // supera il campo degli interi esatti e canali diversi finiscono sullo stesso colore.
    hash = (hash * 31 + seed.charCodeAt(index)) | 0;
  }
  const at = Math.abs(hash) % CHANNEL_COLORS.length;
  return CHANNEL_COLORS[at] as ChannelColor;
}
